/**
 * Provider directory pages (iter 175).
 *
 * Four routes, all reading from /api/providers/directory/*:
 *   /providers                           — landing
 *   /providers/:levelOfCare              — state index for one modality
 *   /providers/:levelOfCare/:state       — verified provider list
 *   /providers/:levelOfCare/:state/:slug — verified provider detail
 *
 * INDEX/NOINDEX behavior — the backend returns `indexable: bool` on
 * every response. When indexable=false we render the page (users are
 * welcome to visit it) but pass `noindex` to Seo so bots ignore it.
 * This is Directive #7 + #22 quality-gate compliance without hiding
 * pages from actual humans.
 *
 * Testids:
 *   providers-landing, provider-level-<slug>,
 *   provider-state-<STATE>, provider-detail-<slug>
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Building2, MapPin, Phone, Globe,
  Loader2, Shield, ShieldCheck, ClipboardCheck,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  organizationSchema, websiteSchema, breadcrumbSchema,
} from '../lib/seoSchemas';
import { absoluteUrl } from '../lib/siteConfig';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────

function DirectoryShell({ children, breadcrumb }) {
  return (
    <div className="min-h-screen bg-cn-bg">
      <PublicNavbar />
      <div className="cn-container pt-6">
        <nav aria-label="Breadcrumb" className="text-xs text-cn-text-muted" data-testid="providers-breadcrumb">
          {breadcrumb.map((b, i) => (
            <React.Fragment key={i}>
              {b.to ? <Link to={b.to} className="hover:text-cn-primary">{b.name}</Link>
                    : <span className="text-cn-text/70">{b.name}</span>}
              {i < breadcrumb.length - 1 && <span className="mx-2">/</span>}
            </React.Fragment>
          ))}
        </nav>
      </div>
      {children}
      <Footer />
    </div>
  );
}

function ClaimCta({ testId = 'claim-cta' }) {
  return (
    <div className="rounded-2xl border border-cn-primary/25 bg-cn-primary/5 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-cn-primary font-semibold mb-1">
        <ShieldCheck className="h-4 w-4" /> Are you this provider?
      </div>
      <p className="text-sm text-cn-text-muted mb-3 leading-relaxed">
        Claim your profile to verify your services, service area, and payers accepted. CaraLoom never charges providers for visibility.
      </p>
      <Link
        to="/contact?type=provider"
        className="cn-btn cn-btn-primary text-sm"
        data-testid="claim-cta-btn"
      >
        Claim this profile <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}

function ThinPageNotice() {
  return (
    <div
      className="rounded-2xl border border-cn-ink/10 bg-white/60 p-5 text-sm text-cn-text-muted"
      data-testid="thin-page-notice"
    >
      <div className="flex items-center gap-2 text-cn-ink font-semibold mb-1">
        <ClipboardCheck className="h-4 w-4 text-cn-primary" /> Directory in progress
      </div>
      We&rsquo;re building this directory listing with verified data only.
      This page is temporarily set to <code className="text-xs bg-cn-soft/60 px-1 py-0.5 rounded">noindex</code> until we&rsquo;ve added enough verified providers. Provider? <Link to="/contact?type=provider" className="text-cn-primary underline">Claim your profile</Link>.
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 1) /providers — landing (with iter 177 NL search)
// ─────────────────────────────────────────────────────────────

function NlSearchBox() {
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/providers/directory/nl-search`, {
        params: { q, limit: 5 },
      });
      setResult(data);
      // High-confidence deep link → route the user directly.
      if (data.query.confidence === 'high' && data.suggested_path) {
        navigate(data.suggested_path);
      }
    } catch (e) {
      setResult({ error: e?.message || 'search failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="cn-container -mt-2 pb-6" data-testid="nl-search">
      <form onSubmit={submit} className="rounded-2xl border border-cn-ink/10 bg-white/80 backdrop-blur-sm p-4 shadow-sm">
        <label htmlFor="nl-search-input" className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold flex items-center gap-1.5 mb-2">
          <ShieldCheck className="h-3 w-3 text-cn-primary" /> Natural-language search
          <span className="text-[10px] normal-case tracking-normal text-cn-text-muted/70">— e.g. &ldquo;hospice near Alexandria accepting Medicare&rdquo;</span>
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="nl-search-input"
            data-testid="nl-search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type your request in plain English…"
            className="flex-1 min-w-[280px] px-3 py-2 rounded-md border border-cn-ink/15 focus:border-cn-primary/60 focus:outline-none text-sm bg-white"
          />
          <button
            type="submit"
            disabled={busy || !q.trim()}
            data-testid="nl-search-submit"
            className="cn-btn cn-btn-primary text-sm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Search <ArrowRight className="h-4 w-4" /></>}
          </button>
        </div>

        {result && !result.error && (
          <div className="mt-4 border-t border-cn-ink/10 pt-4" data-testid="nl-search-result">
            <div className="flex flex-wrap items-baseline gap-2 text-xs text-cn-text-muted mb-3">
              <span className="text-cn-text font-semibold">CaraLoom understood:</span>
              {result.query.matched_terms?.length ? (
                result.query.matched_terms.map((t) => (
                  <span key={t} className="px-1.5 py-0.5 rounded-full bg-cn-primary/10 text-cn-primary text-[10px] uppercase tracking-wider">{t}</span>
                ))
              ) : <span className="italic">nothing recognizable — try &ldquo;hospice in NY&rdquo; or &ldquo;home health with PT&rdquo;.</span>}
              <span className="ml-auto tabular-nums">
                {result.total_matches} verified {result.total_matches === 1 ? 'match' : 'matches'}
              </span>
            </div>
            {result.providers?.length > 0 && (
              <ul className="space-y-1">
                {result.providers.map((p) => (
                  <li key={`${p.level_of_care}-${p.state}-${p.slug}`} className="text-sm">
                    <Link
                      to={`/providers/${p.level_of_care}/${p.state}/${p.slug}`}
                      className="text-cn-primary hover:underline"
                      data-testid={`nl-search-hit-${p.slug}`}
                    >
                      {p.name}
                    </Link>
                    <span className="text-cn-text-muted"> · {p.city}, {p.state}</span>
                  </li>
                ))}
              </ul>
            )}
            {result.suggested_path && (
              <div className="mt-3">
                <Link
                  to={result.suggested_path}
                  className="text-sm font-semibold text-cn-primary inline-flex items-center gap-1"
                  data-testid="nl-search-see-all"
                >
                  See all in the directory <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
          </div>
        )}
        {result?.error && (
          <p className="mt-3 text-sm text-rose-500" data-testid="nl-search-error">
            {result.error}
          </p>
        )}
      </form>
    </section>
  );
}

export function ProvidersLandingPage() {
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/providers/directory/levels`);
        setLevels(data.levels || []);
      } catch {
        setLevels([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const anyIndexable = levels.some((l) => l.verified_provider_count >= 3);

  return (
    <DirectoryShell breadcrumb={[{ name: 'Home', to: '/' }, { name: 'Providers' }]}>
      <Seo
        title="Post-Acute Provider Directory — SNF, Home Health, Hospice, IRF, LTACH | CaraLoom"
        description="Search verified post-acute providers by level of care and state — skilled nursing facilities, home health agencies, hospice, IRF, LTACH, and more. Built for hospital case managers and discharge planners."
        path="/providers"
        keywords={['post acute provider directory', 'skilled nursing facility search', 'home health directory', 'hospice provider directory', 'IRF LTACH directory']}
        noindex={!anyIndexable}
        schema={[
          organizationSchema(),
          websiteSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Providers', path: '/providers' },
          ]),
        ]}
      />
      <section className="cn-container pt-10 pb-6 md:pt-14 md:pb-8" data-testid="providers-landing">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/25 px-3 py-1 text-xs font-semibold text-cn-primary tracking-wide uppercase mb-5">
          <Building2 className="h-3 w-3" /> Provider Directory
        </div>
        <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text tracking-tight leading-[1.05] max-w-3xl">
          Verified post-acute providers &mdash; searchable by level of care.
        </h1>
        <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl">
          Skilled nursing, home health, hospice, IRF, LTACH, and more. Every listing is verified &mdash; no fabricated capabilities, no pay-to-play ranking. Providers claim and own their profiles.
        </p>
      </section>

      <NlSearchBox />

      <section className="cn-container pb-24">
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-cn-primary" />
          </div>
        ) : (
          <>
            {!anyIndexable && <div className="mb-6"><ThinPageNotice /></div>}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {levels.map((l) => (
                <Link
                  key={l.slug}
                  to={`/providers/${l.slug}`}
                  className="cn-card cn-card-hover rounded-2xl p-6 block group"
                  data-testid={`provider-level-${l.slug}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-cn-primary" />
                    <h2 className="font-semibold text-cn-text text-lg group-hover:text-cn-primary transition-colors">
                      {l.name}
                    </h2>
                  </div>
                  <div className="text-xs text-cn-text-muted tabular-nums">
                    {l.verified_provider_count} verified &middot; {l.state_count} state{l.state_count === 1 ? '' : 's'}
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-cn-primary">
                    Browse <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>
    </DirectoryShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 2) /providers/:levelOfCare — state index
// ─────────────────────────────────────────────────────────────

export function ProviderLevelIndexPage() {
  const { levelOfCare } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/providers/directory/${levelOfCare}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [levelOfCare]);

  if (notFound) return <Navigate to="/providers" replace />;

  const name = data?.name || 'Providers';
  const path = `/providers/${levelOfCare}`;

  return (
    <DirectoryShell breadcrumb={[
      { name: 'Home', to: '/' },
      { name: 'Providers', to: '/providers' },
      { name },
    ]}>
      <Seo
        title={`${name} Directory by State | CaraLoom`}
        description={`${name} verified for hospital discharge planning. Search by state to see qualifying, verified ${name.toLowerCase()} in your service area.`}
        path={path}
        keywords={[`${name.toLowerCase()} directory`, `${name.toLowerCase()} by state`, 'post acute provider search']}
        noindex={!data?.indexable}
        schema={[
          organizationSchema(),
          websiteSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Providers', path: '/providers' },
            { name, path },
          ]),
        ]}
      />
      <section className="cn-container pt-10 pb-6 md:pt-14 md:pb-8">
        <h1 className="cn-display text-3xl sm:text-4xl lg:text-5xl text-cn-text tracking-tight leading-[1.05] max-w-3xl">
          {name} &mdash; by state.
        </h1>
        {data && (
          <p className="mt-6 text-base text-cn-text-muted max-w-2xl">
            {data.verified_provider_count} verified {name.toLowerCase()} across {data.state_count} state{data.state_count === 1 ? '' : 's'}.
          </p>
        )}
      </section>

      <section className="cn-container pb-24">
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-cn-primary" />
          </div>
        ) : (
          <>
            {!data?.indexable && <div className="mb-6"><ThinPageNotice /></div>}
            {(data?.states || []).length === 0 ? (
              <p className="text-sm text-cn-text-muted italic" data-testid="empty-state-list">
                No verified providers listed yet for this level of care. Providers can <Link to="/contact?type=provider" className="text-cn-primary underline">claim a profile</Link> to be listed.
              </p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(data?.states || []).map((s) => (
                  <Link
                    key={s.state}
                    to={`/providers/${levelOfCare}/${s.state}`}
                    className="cn-card cn-card-hover rounded-xl p-4 block"
                    data-testid={`provider-state-${s.state}`}
                  >
                    <div className="flex items-center gap-2 text-cn-primary font-semibold">
                      <MapPin className="h-3.5 w-3.5" /> {s.state}
                    </div>
                    <div className="text-xs text-cn-text-muted mt-1 tabular-nums">
                      {s.verified_provider_count} verified
                      {!s.indexable && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-600">building</span>}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </DirectoryShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 3) /providers/:levelOfCare/:state — verified list
// ─────────────────────────────────────────────────────────────

export function ProviderStatePage() {
  const { levelOfCare, state } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/providers/directory/${levelOfCare}/${state}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 404 || e?.response?.status === 400) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [levelOfCare, state]);

  if (notFound) return <Navigate to={`/providers/${levelOfCare}`} replace />;

  const path = `/providers/${levelOfCare}/${(state || '').toUpperCase()}`;
  const locName = data?.level_of_care_name || 'Providers';

  return (
    <DirectoryShell breadcrumb={[
      { name: 'Home', to: '/' },
      { name: 'Providers', to: '/providers' },
      { name: locName, to: `/providers/${levelOfCare}` },
      { name: (state || '').toUpperCase() },
    ]}>
      <Seo
        title={`${locName} in ${(state || '').toUpperCase()} | CaraLoom`}
        description={`Verified ${locName.toLowerCase()} in ${(state || '').toUpperCase()} for hospital discharge planning. See services, service area, and contact information.`}
        path={path}
        keywords={[`${locName.toLowerCase()} in ${(state || '').toLowerCase()}`, 'post acute providers', 'discharge planning']}
        noindex={!data?.indexable}
        schema={[
          organizationSchema(),
          websiteSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Providers', path: '/providers' },
            { name: locName, path: `/providers/${levelOfCare}` },
            { name: (state || '').toUpperCase(), path },
          ]),
        ]}
      />
      <section className="cn-container pt-10 pb-6 md:pt-14 md:pb-8">
        <h1 className="cn-display text-3xl sm:text-4xl lg:text-5xl text-cn-text tracking-tight leading-[1.05] max-w-3xl">
          {locName} in {(state || '').toUpperCase()}.
        </h1>
        {data && (
          <p className="mt-6 text-base text-cn-text-muted max-w-2xl">
            {data.verified_provider_count} verified {locName.toLowerCase()} listed.
          </p>
        )}
      </section>

      <section className="cn-container pb-24">
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-cn-primary" />
          </div>
        ) : (
          <>
            {!data?.indexable && <div className="mb-6"><ThinPageNotice /></div>}
            {(data?.providers || []).length === 0 ? (
              <p className="text-sm text-cn-text-muted italic" data-testid="empty-state-list">
                No verified providers listed yet for this state and level of care.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data?.providers || []).map((p) => (
                  <Link
                    key={p.slug}
                    to={`/providers/${levelOfCare}/${(state || '').toUpperCase()}/${p.slug}`}
                    className="cn-card cn-card-hover rounded-2xl p-5 block group"
                    data-testid={`provider-card-${p.slug}`}
                  >
                    <div className="font-semibold text-cn-text group-hover:text-cn-primary transition-colors">
                      {p.name}
                    </div>
                    <div className="text-xs text-cn-text-muted mt-1 inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {p.city}, {p.state} {p.zip || ''}
                    </div>
                    {p.star_rating && (
                      <div className="text-xs text-cn-text-muted mt-1">CMS: {p.star_rating.toFixed(1)}\u2606</div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </DirectoryShell>
  );
}

// ─────────────────────────────────────────────────────────────
// 4) /providers/:levelOfCare/:state/:slug — detail
// ─────────────────────────────────────────────────────────────

export function ProviderDetailPage() {
  const { levelOfCare, state, slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/providers/directory/${levelOfCare}/${state}/${slug}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [levelOfCare, state, slug]);

  if (notFound) return <Navigate to={`/providers/${levelOfCare}/${(state || '').toUpperCase()}`} replace />;

  const p = data?.provider;
  const locName = data?.level_of_care_name || 'Provider';
  const path = `/providers/${levelOfCare}/${(state || '').toUpperCase()}/${slug}`;

  const providerSchema = p && {
    '@context': 'https://schema.org',
    '@type': ['MedicalOrganization', 'LocalBusiness'],
    '@id': `${absoluteUrl(path)}#provider`,
    name: p.name,
    url: absoluteUrl(path),
    telephone: p.phone || undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: p.address_line_1 || undefined,
      addressLocality: p.city,
      addressRegion: p.state,
      postalCode: p.zip || undefined,
      addressCountry: 'US',
    },
    identifier: p.npi || p.cms_id || undefined,
    medicalSpecialty: locName,
  };

  return (
    <DirectoryShell breadcrumb={[
      { name: 'Home', to: '/' },
      { name: 'Providers', to: '/providers' },
      { name: locName, to: `/providers/${levelOfCare}` },
      { name: (state || '').toUpperCase(), to: `/providers/${levelOfCare}/${(state || '').toUpperCase()}` },
      { name: p?.name || slug },
    ]}>
      <Seo
        title={`${p?.name || 'Provider'} \u2014 ${locName} in ${(state || '').toUpperCase()} | CaraLoom`}
        description={p ? `${p.name}, a verified ${locName.toLowerCase()} in ${p.city}, ${p.state}. Services, service area, and contact information.` : ''}
        path={path}
        // Detail pages inherit the state page's indexable state — every
        // provider in a >= threshold state page is indexable, otherwise
        // noindex until the state's overall verified count catches up.
        noindex={false}
        schema={p ? [providerSchema, breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Providers', path: '/providers' },
          { name: locName, path: `/providers/${levelOfCare}` },
          { name: (state || '').toUpperCase(), path: `/providers/${levelOfCare}/${(state || '').toUpperCase()}` },
          { name: p.name, path },
        ])] : []}
      />

      <section className="cn-container pt-10 pb-16" data-testid="provider-detail">
        {loading ? (
          <div className="p-16 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-cn-primary" />
          </div>
        ) : p ? (
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-cn-primary/10 border border-cn-primary/25 px-3 py-1 text-[11px] font-semibold text-cn-primary tracking-wider uppercase mb-4">
                <ShieldCheck className="h-3 w-3" /> Verified {locName.toLowerCase().replace(/s$/, '')}
              </div>
              <h1 className="cn-display text-3xl sm:text-4xl text-cn-text leading-tight" data-testid={`provider-detail-${slug}`}>
                {p.name}
              </h1>
              <div className="mt-4 text-sm text-cn-text-muted flex flex-wrap gap-x-6 gap-y-2">
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[p.address_line_1, `${p.city}, ${p.state}`, p.zip].filter(Boolean).join(', ')}</span>
                {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {p.phone}</span>}
                {p.website && <a href={p.website} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-cn-primary hover:underline"><Globe className="h-3.5 w-3.5" /> Website</a>}
              </div>

              {p.verified_capabilities?.length > 0 && (
                <section className="mt-8">
                  <h2 className="font-semibold text-cn-text mb-3">Verified capabilities</h2>
                  <div className="flex flex-wrap gap-2">
                    {p.verified_capabilities.map((c) => (
                      <span key={c} className="text-xs px-2 py-1 rounded-full bg-cn-soft/60 border border-cn-ink/10 text-cn-text-muted">{c}</span>
                    ))}
                  </div>
                </section>
              )}

              {p.payers_accepted?.length > 0 && (
                <section className="mt-6">
                  <h2 className="font-semibold text-cn-text mb-3">Payers accepted</h2>
                  <div className="flex flex-wrap gap-2">
                    {p.payers_accepted.map((c) => (
                      <span key={c} className="text-xs px-2 py-1 rounded-full bg-cn-soft/60 border border-cn-ink/10 text-cn-text-muted">{c}</span>
                    ))}
                  </div>
                </section>
              )}

              {p.service_area?.length > 0 && (
                <section className="mt-6">
                  <h2 className="font-semibold text-cn-text mb-3">Service area</h2>
                  <p className="text-sm text-cn-text-muted">{p.service_area.join(', ')}</p>
                </section>
              )}

              {p.source && (
                <p className="mt-8 text-[11px] text-cn-text-muted italic">
                  Source: {p.source}. Last verified {p.verified_at ? new Date(p.verified_at).toLocaleDateString() : '\u2014'}.
                </p>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-4">
              <ClaimCta />
              <div className="rounded-2xl border border-cn-ink/10 bg-cn-soft/40 p-5">
                <h3 className="font-semibold text-cn-text mb-2">Discharging a patient?</h3>
                <p className="text-sm text-cn-text-muted mb-3 leading-relaxed">
                  Send a HIPAA-safe referral packet directly from CaraLoom &mdash; with coverage checked first.
                </p>
                <Link to="/contact?type=demo" className="cn-btn cn-btn-primary w-full justify-center text-sm">
                  Request a demo <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link to="/discharge-planning-software" className="cn-btn cn-btn-ghost w-full justify-center text-sm mt-2">
                  See the platform <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </aside>
          </div>
        ) : null}
      </section>
    </DirectoryShell>
  );
}

// Convenience default so App.js can `import ProvidersLandingPage from '...'`.
export default ProvidersLandingPage;
